import { Component, EventEmitter, Input, Output, ChangeDetectionStrategy } from '@angular/core';
import { ColorPickerModule } from '@syncfusion/ej2-angular-inputs';
import { TranslatePipe } from '@ngx-translate/core';

type ColorPickerPosition = 'left' | 'right';

@Component({
  selector: 'app-color-picker-field',
  templateUrl: './color-picker-field.component.html',
  styleUrls: ['./color-picker-field.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [ColorPickerModule, TranslatePipe]
})
export class ColorPickerFieldComponent {
  @Input() public id = '';
  @Input() public labelKey = '';
  @Input() public value: string | null = '';
  @Input() public placeholder = '#RRGGBB';
  @Input() public hintKey = 'i18n.base.shared.color-picker-field.hint';
  @Input() public pickerPosition: ColorPickerPosition = 'left';
  @Input() public showValueInput = true;
  @Input() public showLabel = true;
  @Input() public showHint = true;

  @Output() public valueChange = new EventEmitter<string>();

  public onColorPickerChanged(eventArgs: unknown): void {
    const nextValue = this.extractColorPickerHexValue(eventArgs);
    if (nextValue === null) {
      return;
    }

    this.valueChange.emit(nextValue);
  }

  public onHexInput(eventArgs: Event): void {
    const inputElement = eventArgs.target as HTMLInputElement | null;
    const normalizedValue = this.normalizeHexInput(inputElement?.value ?? '');

    if (inputElement && inputElement.value !== normalizedValue) {
      inputElement.value = normalizedValue;
    }

    this.valueChange.emit(normalizedValue);
  }

  public get pickerValue(): string {
    return this.isHexColor(this.value) ? String(this.value) : '';
  }

  public get isPickerOnRight(): boolean {
    return this.pickerPosition === 'right';
  }

  private extractColorPickerHexValue(eventArgs: unknown): string | null {
    if (!eventArgs || typeof eventArgs !== 'object') {
      return null;
    }

    const eventRecord = eventArgs as Record<string, unknown>;
    const directValue = typeof eventRecord.value === 'string' ? eventRecord.value.trim() : '';

    if (directValue === '') {
      return '';
    }

    if (this.isHexColor(directValue)) {
      return directValue;
    }

    const currentValue = eventRecord.currentValue as Record<string, unknown> | undefined;
    const hexValue = typeof currentValue?.hex === 'string' ? currentValue.hex.trim() : '';
    if (hexValue === '') {
      return '';
    }

    return this.isHexColor(hexValue) ? hexValue : null;
  }

  private isHexColor(value: unknown): boolean {
    return typeof value === 'string' && /^#([0-9A-Fa-f]{3}|[0-9A-Fa-f]{6})$/.test(value.trim());
  }

  private normalizeHexInput(rawValue: string): string {
    const trimmedValue = rawValue.trim();
    if (!trimmedValue) {
      return '';
    }

    if (trimmedValue === '#') {
      return '#';
    }

    const withoutHash = trimmedValue.replace(/^#/, '');
    const hexOnly = withoutHash.replace(/[^0-9A-Fa-f]/g, '').slice(0, 6);

    if (!hexOnly) {
      return '';
    }

    return `#${hexOnly}`;
  }
}
